import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WbCard, WbGrid, WbGridCol, WbGridRow, WbHeading, WbText } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import { _getFreemiumImages, _getUrlImage } from '../../../Utils/Vimdt/apiCalls';
import { marketList } from '../../../Constants/Vimdt/imagesMarketList';
import _notification from '../../../Utils/Vimdt/notification';
import _handleErrors from '../../../Utils/Vimdt/handleErrors';

import VimdtButton from '../Common/VimdtButton';
import VimdtSelect from '../Common/VimdtSelect';
import VimdtAccordion from '../Common/VimdtAccordion';
import PanelHeader from '../Common/PanelHeader';
import ModelsImageList from './ModelsImageList';
import './Models.scss';

const BRANDS = [
	{ key: '4', label: 'Mercedes-Benz' },
	{ key: '5', label: 'Mercedes-EQ' },
	{ key: '6', label: 'Mercedes-AMG' },
	{ key: '7', label: 'Mercedes-Maybach' },
];

const SUB_BRANDS = ['Mercedes-AMG', 'Mercedes-EQ', 'Mercedes-Maybach'];

const convertModelImage = (imageData) => {
	const blob = new Blob([new Uint8Array(imageData)]);
	const urlCreator = window.URL || window.webkitURL;
	return { url: urlCreator.createObjectURL(blob), blob };
};

const revokeImageUrls = (list) => {
	(list || []).forEach(({ url }) => url && (window.URL || window.webkitURL).revokeObjectURL(url));
};

function Models({ rowCount = 6 }) {
	const { t } = useTranslation();

	const [marketId, setMarketId] = useState('de_DE');
	const [saveFileName, setSaveFileName] = useState(null);
	const [imageList, setImageList] = useState([]);
	const [isImagesLoading, setIsImagesLoading] = useState(false);
	const [settingsPanels, setSettingsPanels] = useState([]);
	const [panels, setPanels] = useState([]);
	const [innerPanels, setInnerPanels] = useState([]);

	const prevImageList = useRef([]);
	const notifyKey = useRef();

	const hasImages = imageList.length > 0;
	
	useEffect(() => {
		const expand = hasImages && !isImagesLoading;
		setPanels(expand ? ['1', '2', '3'] : []);
		setInnerPanels(expand ? BRANDS.map(({ key }) => key) : []);
	}, [hasImages, isImagesLoading]);

	useEffect(() => {
		prevImageList.current = imageList;
	}, [imageList]);

	useEffect(
		() => () => revokeImageUrls(prevImageList.current),
		[]
	);

	const openNotification = (title, message) => {
		notifyKey.current = _notification({
			title,
			message,
			key: notifyKey.current,
			onClose: () => {
				notifyKey.current = undefined;
			},
		});
	};

	const getFreemiumImage = (imageUrl) =>
		_getUrlImage({ imageUrl })
			.then((resp) => resp.data);

	const loadImages = async (newImageList) =>
		newImageList.reduce(async (previousCall, nextImage) => {
			await previousCall;
			const { url, title, type } = nextImage;
			try {
				const imageData = await getFreemiumImage(url);
				setImageList([...prevImageList.current, { title, type, ...convertModelImage(imageData) }]);
			} catch (err) {
				_notification({
					title: t('vimdt.notification.error'),
					message: t('vimdt.notification.failedImage') + title,
					error: true,
				});
				console.error(err);
			}
		}, Promise.resolve());

	const getFreemiumImages = async () => {
		setIsImagesLoading(true);
		setSaveFileName(`Freemium Images - ${marketId.split('_')[1]}`);

		try {
			const resp = await _getFreemiumImages({ marketId });
			const newImageList = Object.values(resp.data || {}).map((item) => ({
				title: item.title,
				url: item.url,
				type: item.type,
			}));

			revokeImageUrls(prevImageList.current);
			setImageList([]);
			await loadImages(newImageList);
			setIsImagesLoading(false);
			openNotification(
				t('vimdt.notification.finished'),
				t('vimdt.notification.loadedImages') + marketId.split('_')[1]
			);
		} catch (err) {
			setIsImagesLoading(false);
			openNotification(
				t('vimdt.notification.error'),
				_handleErrors(err, marketId.split('_')[1])
			);
		}
	};

	const zipAndSave = (addFiles) => {
		const zip = new JSZip();
		addFiles(zip);
		zip
			.generateAsync({ type: 'blob' })
			.then((content) => saveAs(content, `${saveFileName}.zip`))
			.catch((err) => console.error(err));
	};

	const saveImages = () =>
		zipAndSave((zip) => {
			const imageFolder = zip.folder(saveFileName);
			imageList.forEach(({ title, blob }) => imageFolder.file(`${title}.png`, blob));
		});

	const saveImagesByFile = () =>
		zipAndSave((zip) => {
			const folders = {
				PASSENGER_CAR: imageList.some((i) => i.type === 'PASSENGER_CAR')
					? zip.folder('Cars')
					: null,
				VAN: imageList.some((i) => i.type === 'VAN') ? zip.folder('Vans') : null,
				SMART: imageList.some((i) => i.type === 'SMART') ? zip.folder('Smart') : null,
			};

			imageList.forEach(({ type, title, blob }) => {
				folders[type]?.file(`${title}.png`, blob);
			});
		});

	const byType = (type) => imageList.filter((obj) => obj.type === type);

	const carsByBrand = (brand) =>
		byType('PASSENGER_CAR').filter((obj) =>
			brand === 'Mercedes-Benz'
				? !SUB_BRANDS.some((sub) => obj.title.includes(sub))
				: obj.title.includes(brand)
		);

	const brandItems = BRANDS.map(({ key, label }) => {
		const images = carsByBrand(label);
		return images.length > 0
			? {
					key,
					header: label,
					content: <ModelsImageList imageList={images} imageType="png" rowCount={rowCount} />,
			  }
			: null;
	}).filter(Boolean);

	const topItems = [
		brandItems.length > 0 && {
			key: '1',
			header: 'Mercedes-Benz Cars',
			content: (
				<VimdtAccordion items={brandItems} activeKeys={innerPanels} onChange={setInnerPanels} />
			),
		},
		byType('VAN').length > 0 && {
			key: '2',
			header: 'Mercedes-Benz Vans',
			content: (
				<ModelsImageList imageList={byType('VAN')} imageType="png" rowCount={rowCount} />
			),
		},
		byType('SMART').length > 0 && {
			key: '3',
			header: 'Smart',
			content: (
				<ModelsImageList imageList={byType('SMART')} imageType="png" rowCount={rowCount} />
			),
		},
	].filter(Boolean);

	const count = imageList.length;

	return (
		<div className="vimdt-models">
			<WbCard className="shared-tab-content-padding">
				<WbHeading size="m">{t('vimdt.modelsTab.downloadText')}</WbHeading>

				<VimdtAccordion
					activeKeys={settingsPanels}
					onChange={setSettingsPanels}
					items={[
						{
							key: 'settings',
							header: (
								<PanelHeader
									headerName={t('vimdt.modelsTab.modelsContentSettings')}
									description={t('vimdt.modelsTab.modelsContentSettingsDesc')}
									data={[
										{
											key: 1,
											type: t('vimdt.components.selectMarket'),
											desc: t('vimdt.modelsTab.infoTable.selectMarket'),
										},
									]}
								/>
							),
							content: (
								<WbGrid fullWidth>
									<WbGridRow>
										<WbGridCol mq1={12} mq3={6}>
											<VimdtSelect
												label={t('vimdt.components.selectMarket')}
												value={marketId}
												setValue={setMarketId}
												adjustableWith
												options={marketList.map((market) => ({
													id: market.market_code,
													value: market.market_code,
													name: market.market_name,
												}))}
											/>
										</WbGridCol>
									</WbGridRow>
								</WbGrid>
							),
						},
					]}
				/>

				<div className="vimdt-models__actions">
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadImages')}
						onClick={getFreemiumImages}
						loading={isImagesLoading}
					/>
				</div>
			</WbCard>

			{count > 0 && (
				<div className="vimdt-models__header">
					<WbText strong>{`${count} ${
						count > 1 ? t('vimdt.infoTexts.imagesFound') : t('vimdt.infoTexts.imageFound')
					}`}</WbText>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.saveImages')}
						filledIn={false}
						onClick={saveImages}
					/>
				</div>
			)}

			<VimdtAccordion items={topItems} activeKeys={panels} onChange={setPanels} />

			{count > 0 && (
				<div className="vimdt-models__footer">
					<VimdtButton
						label={t('vimdt.loadSaveButtons.saveImages')}
						filledIn={false}
						onClick={saveImagesByFile}
					/>
				</div>
			)}
		</div>
	);
}

export default Models;
